import React, {useEffect, useState} from 'react';
import Translate from "../../../../../components/Translate";
import {formatSecondsToHHMMSS, getTodayDate, getTomorrowDate} from "../../../../../utils/General";
import config from "../../../../../config";
import RatingStars from "../../../../../components-new/general/RatingStars";
import LevelBadge from "../../../../../components-new/general/LevelBadge";
import Tooltip from "../../../../../components-new/general/Tooltip";
import {getMyScore, getOperatorsTimes, getStatisticNumbers} from "../../../../../services/statistics/api";
import StatisticCard from "../../../../../components-new/general/statistics/StatisticCard";
import ForwardIcon from "../../../../../assets/icons/ForwardIcon";
import Colors from "../../../../../utils/Colors";
import XIcon from "../../../../../assets/icons/XIcon";
import CheckIcon from "../../../../../assets/icons/CheckIcon";
import WarningIcon from "../../../../../assets/icons/WarningIcon";
import ColorIndicator from "../../../../../components-new/general/ColorIndicator";
import DatePicker from "../../../../../components-new/form/DatePicker";

const OperatorStatistics = ({
                                company,
                                user,
                                currentStatusStartDate,
                                currentStatusEndDate,
                                setCurrentStatusStartDate,
                                setCurrentStatusEndDate,
                                dailyChatsLimit,
                                currentChatsLimit,
                                currentStatusHistory,
                                openTrModal
                            }) => {

    const subs = company.subs ? company.subs : [];

    const ratingPercent = Number(user.rating) || 0;
    const ratingCount = Number(user.ratingCount) || 0;
    const ratingInStars = (ratingPercent * 5) / 100;

    const [isFetchingNumbers, setIsFetchingNumbers] = useState(false);
    const [statNumbers, setStatNumbers] = useState({
        allRequests: 0,
        todaysRequests: 0,
        answeredRequests: 0,
        unansweredRequests: 0,
        activeRequests: 0,
        forwardedRequests: 0
    });

    const [isFetchingTimes, setIsFetchingTimes] = useState(false);
    const [operatorTimes, setOperatorTimes] = useState({
        avgResponseTime: null,
        avgFirstAnswerTime: null,
        avgConversationTime: null
    })
    const [operatorTimesStart, setOperatorTimesStart] = useState(null);
    const [operatorTimesEnd, setOperatorTimesEnd] = useState(null);

    useEffect(() => {
        fetchNumbers()

        const interval = setInterval(fetchNumbers, 30000)

        return () => clearInterval(interval)
    }, [])

    useEffect(fetchTimes, [operatorTimesStart, operatorTimesEnd])

    /*
     * Asked for, not worked out.
     *
     * This screen had its own copy of the formula, assembled from three separate
     * requests - the numbers, the times, the rating. The server answers with the
     * level now, so the badge somebody sees about themselves is the same badge
     * their manager sees in the statistics. They disagreed before, because the
     * two were reading different periods through the same formula.
     */
    const [rank, setRank] = useState(1);

    useEffect(() => {
        let active = true;

        getMyScore()
            .then(result => {
                if (active && result) setRank(result.level);
            })
            .catch(() => {
                /* the badge is the one thing on this page that can be missing
                   without the page being wrong */
            });

        return () => {
            active = false;
        };
    }, [])

    function fetchNumbers() {
        if (isFetchingNumbers) return;

        setIsFetchingNumbers(true);
        getStatisticNumbers(subs.map(sub => sub.id), user.postgresId).then(result => {
            if (result) {
                setStatNumbers(result);
            }
        }).finally(() => {
            setIsFetchingNumbers(false);
        })
    }

    function fetchTimes() {
        if (isFetchingTimes) return;
        setIsFetchingTimes(true);

        getOperatorsTimes(subs.map(sub => sub.id), [user.postgresId], operatorTimesStart, operatorTimesEnd).then(result => {
            if (result && result[0]) {
                setOperatorTimes({
                    avgResponseTime: parseInt(result[0].avgResponseTime),
                    avgFirstAnswerTime: parseInt(result[0].avgFirstAnswerTime),
                    avgConversationTime: parseInt(result[0].avgConversationTime)
                });
            } else {
                setOperatorTimes({
                    avgResponseTime: null,
                    avgFirstAnswerTime: null,
                    avgConversationTime: null
                })
            }
        }).finally(() => {
            setIsFetchingTimes(false);
        })
    }


    return (
        <React.Fragment>
            <div className="stat-card-group stat-card-group--counters">
            <StatisticCard
                onClick={() => openTrModal('allRequestsToOperator')}
                title={'allRequestsToOperator'}
                value={statNumbers.allRequests}
                loading={isFetchingNumbers}
            />

            <StatisticCard
                onClick={() => openTrModal('todaysRequestsToOperator')}
                title={'todaysRequestsToOperator'}
                value={statNumbers.todaysRequests}
                loading={isFetchingNumbers}
            />

            <StatisticCard
                onClick={() => openTrModal('activeRequests', {statuses: [config.conversationStatuses.operatorProcessing]})}
                title={'activeRequests'}
                value={statNumbers.activeRequests}
                loading={isFetchingNumbers}
            />

            <StatisticCard
                onClick={() => openTrModal('answeredRequestsToOperator', {unanswered: false})}
                title={'answeredRequestsToOperator'}
                value={statNumbers.answeredRequests}
                loading={isFetchingNumbers}

                icon={<CheckIcon style={{color: Colors.blueDark}} stroke="currentColor"/>}
            />

            <StatisticCard
                onClick={() => openTrModal('unansweredRequestsToOperator', {unanswered: true})}
                title={'unansweredRequestsToOperator'}
                value={statNumbers.unansweredRequests}
                loading={isFetchingNumbers}

                icon={<XIcon style={{color: Colors.blueDark}} stroke="currentColor"/>}
            />

            <StatisticCard
                onClick={() => openTrModal('forwardedRequestsToOperator', {forwarded: true})}
                title={'forwardedRequestsToOperator'}
                value={statNumbers.forwardedRequests}
                loading={isFetchingNumbers}

                icon={<ForwardIcon style={{color: Colors.blueDark}} stroke="currentColor"/>}
            />
            </div>

            <div className="stat-card-group stat-card-group--operator-details">
            <div className="stat-tile">
                <div className="stat-tile__head">
                    <span className="stat-tile__label"><Translate text={'Rating'}/></span>
                </div>

                <div className="stat-rating">
                    <span className="stat-tile__value">
                        <Tooltip text={'Rating'}>{ratingPercent}%</Tooltip>
                        <span className="stat-rating__separator">/</span>
                        <Tooltip text={'ratingCount'}>{ratingCount}</Tooltip>
                    </span>
                    <RatingStars value={ratingInStars} starSize="small"/>
                </div>

                <div className="stat-tile__rank">
                    {/* every level carries a mark now, not only 6 and up - the
                        people who most need to see that the ladder continues were
                        the ones getting a bare number */}
                    <LevelBadge level={rank} withLabel/>
                </div>
            </div>

            <StatisticCard
                title={'dailyChatsLimit'}
                icon={<WarningIcon style={{color: Colors.blueDark}} stroke="currentColor"/>}
                value={dailyChatsLimit !== ''
                    ? (dailyChatsLimit >= 999 ? <Translate text={'unlimited'}/> : dailyChatsLimit)
                    : '...'}
            />

            <StatisticCard
                title={'currentChatLimit'}
                icon={<WarningIcon style={{color: Colors.blueDark}} stroke="currentColor"/>}
                value={currentChatsLimit !== ''
                    ? (currentChatsLimit >= 999 ? <Translate text={'unlimited'}/> : currentChatsLimit)
                    : '...'}
            />
            </div>


            <div className="stat-section">
                <div className="stat-section__head">
                    <h2 className="stat-section__title"><Translate text={'operatorTimeStatistics'}/></h2>

                    <div className="stat-section__filters">
                        <DatePicker
                            inputContainerStyle={{width: '200px', height: '36px'}}
                            value={operatorTimesStart}
                            onChange={(e) => setOperatorTimesStart(e)}
                            onReset={() => setOperatorTimesStart(null)}
                            placeholder={'startDate'}
                            disabled={isFetchingTimes}
                            panelAlign={{horizontal: 'right', vertical: ''}}
                        />

                        <DatePicker
                            inputContainerStyle={{width: '200px', height: '36px'}}
                            value={operatorTimesEnd}
                            onChange={e => setOperatorTimesEnd(e)}
                            placeholder={'endDate'}
                            endOfDay
                            disabled={isFetchingTimes}
                            onReset={() => setOperatorTimesEnd(null)}
                        />
                    </div>
                </div>

                <div className="stat-section__body">
                    <StatisticCard
                        title={'avrgAnswerSpeed'}
                        icon={'noIcon'}
                        loading={isFetchingTimes}
                        value={operatorTimes.avgResponseTime
                            ? formatSecondsToHHMMSS(operatorTimes.avgResponseTime)
                            : 'N/A'}
                    />

                    <StatisticCard
                        title={'avrgFirstAnswerSpeed'}
                        icon={'noIcon'}
                        loading={isFetchingTimes}
                        value={operatorTimes.avgFirstAnswerTime
                            ? formatSecondsToHHMMSS(operatorTimes.avgFirstAnswerTime)
                            : 'N/A'}
                    />

                    <StatisticCard
                        title={'avrgConvTime'}
                        icon={'noIcon'}
                        loading={isFetchingTimes}
                        value={operatorTimes.avgConversationTime
                            ? formatSecondsToHHMMSS(operatorTimes.avgConversationTime)
                            : 'N/A'}
                    />
                </div>
            </div>

            <div className="stat-section">
                <div className="stat-section__head">
                    <h2 className="stat-section__title"><Translate text={'currentStatusHistory'}/></h2>

                    <div className="stat-section__filters">
                        <DatePicker
                            inputContainerStyle={{width: '200px', height: '36px'}}
                            value={currentStatusStartDate}
                            onChange={(e) => setCurrentStatusStartDate(e)}
                            onReset={() => setCurrentStatusStartDate(getTodayDate())}
                            placeholder={'startDate'}
                            defaultValue={getTodayDate()}
                            panelAlign={{horizontal: 'right', vertical: ''}}
                        />

                        <DatePicker
                            inputContainerStyle={{width: '200px', height: '36px'}}
                            value={currentStatusEndDate}
                            onChange={e => setCurrentStatusEndDate(e)}
                            placeholder={'endDate'}
                            endOfDay
                            defaultValue={getTomorrowDate()}
                            onReset={() => setCurrentStatusEndDate(getTomorrowDate())}
                        />
                    </div>
                </div>

                <div className="stat-section__body">
                    {Object.entries(config.currentStatusMap).map(([key, status]) => (
                        <div key={status} className="stat-tile">
                            <div className="stat-tile__head">
                                <span className="stat-tile__icon stat-tile__icon--bare">
                                    <ColorIndicator currentStatus={status}/>
                                </span>
                                <span className="stat-tile__label"><Translate text={key}/></span>
                            </div>
                            <div className="stat-tile__value stat-tile__value--text">
                                {currentStatusHistory[status] ? formatSecondsToHHMMSS(currentStatusHistory[status]) : '00:00:00'}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

        </React.Fragment>
    );
};

export default OperatorStatistics;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/my-history/components/OperatorStatistics.jsx